import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingIndicator, toast } from "@jsq/ds/core";
import { useIdentifyArena } from "@jsq/flags-react";
import { PaymentMethod } from "#common/enums";
import { formatError, useReportError } from "#common/hooks/useReportError";
import {
  useArenaPayeeSupportedPaymentMethodsQuery,
  useGetCountryCodesQuery,
} from "#graphql/supergraphHooks";
import { clientContext, toDbId } from "#utils/graphql_utils";
import { useBasePayeeReducerContext } from "../../../PayeeWorkflows/Context/PayeeContext";
import { WorkflowMode } from "../../common/enums";
import { Arena } from "../../common/types";
import { getPaymentMethod } from "../../common/utils";
import { PayeePaymentPrefCanopyAutoConfirmation } from "../../Components/PayeeCreatePaymentPrefAutoConfirmation";
import { PayeeEditPaymentPrefActions } from "../../Components/PayeeEditPaymentPrefActions";
import { type ChildComponentProps } from "../../Components/types";
import { ConsolidatedPaymentPrefProvider } from "../../ConsolidatedPaymentPrefContextProvider";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { getAvailablePaymentMethods, getSingleAvailablePaymentMethod } from "../../mappers";
import { BasePaymentPrefWorkflow } from "../BaseWorkflow";
import { PayeePaymentPrefWorkflowProps } from "./types";
import {
  createInitialAchPaymentPrefFromPayee,
  createInitialWirePaymentPrefFromPayee,
} from "./utils";

/**
 * Inner component that wraps BasePaymentPrefWorkflow and has access to the context
 */
const PayeeWorkflowWithContext: React.FC<{
  paymentPref?: PayeePaymentPrefWorkflowProps["paymentPref"];
  payeeName?: string;
  payeeAddress?: ReturnType<typeof useBasePayeeReducerContext>["payeeAddress"];
  countryData: ReturnType<typeof useGetCountryCodesQuery>["data"];
  isEditMode: boolean;
  onExit: () => void;
  onSuccess: () => void;
}> = ({ paymentPref, payeeName, payeeAddress, countryData, isEditMode, onExit, onSuccess }) => {
  const { setPaymentPrefDetail } = useConsolidatedPaymentPrefContext();

  /**
   * Callback invoked before step transition to prepare payment preference detail
   * This ensures the data is ready before the next step mounts
   */
  const handleBeforeContinue = useCallback(
    (method: PaymentMethod) => {
      const originalPaymentMethod = paymentPref ? getPaymentMethod(paymentPref) : undefined;

      // In edit mode, only re-initialize if payment method changed
      if (isEditMode && method === originalPaymentMethod) {
        return; // Keep existing payment pref detail
      }

      // Initialize new payment pref detail for the selected method
      if (method === PaymentMethod.ACH) {
        const achDetail = createInitialAchPaymentPrefFromPayee(payeeName);
        setPaymentPrefDetail(achDetail);
      } else if (method === PaymentMethod.Wire) {
        const wireDetail = createInitialWirePaymentPrefFromPayee(
          payeeName,
          payeeAddress,
          countryData,
        );
        setPaymentPrefDetail(wireDetail);
      }
    },
    [payeeName, payeeAddress, countryData, isEditMode, paymentPref, setPaymentPrefDetail],
  );

  /**
   * In edit mode, show the PayeeEditPaymentPrefActions component first
   * This allows users to choose between editing instructions or switching payment method
   */
  const prefixComponents = useMemo<React.FC<ChildComponentProps>[] | undefined>(() => {
    if (isEditMode && paymentPref) {
      return [
        (props: ChildComponentProps) => (
          <PayeeEditPaymentPrefActions {...props} paymentPref={paymentPref} />
        ),
      ];
    }
    return undefined;
  }, [isEditMode, paymentPref]);

  return (
    <BasePaymentPrefWorkflow
      workflowMode={isEditMode ? WorkflowMode.Edit : WorkflowMode.Create}
      prefixComponents={prefixComponents}
      suffixComponents={[PayeePaymentPrefCanopyAutoConfirmation]}
      onBeforeContinue={handleBeforeContinue}
      onExit={onExit}
      onSuccess={onSuccess}
      allowPaymentMethodChange={isEditMode}
    />
  );
};

export const PayeeCreatePaymentPrefWorkflow: React.FC<PayeePaymentPrefWorkflowProps> = ({
  arenaId,
  paymentPref,
  onExit,
  onSuccess,
}) => {
  const { payeeName, payeeAddress } = useBasePayeeReducerContext();
  const identifyArena = useIdentifyArena();

  // Track when the Unleash context has been updated for this arena so that
  // arena-scoped flags read by this workflow and its children (e.g.
  // internationalPaymentRails in BaseWorkflow) are not evaluated against the
  // unscoped default.
  const [isArenaIdentified, setIsArenaIdentified] = useState(false);

  useEffect(() => {
    void identifyArena(toDbId(arenaId)).then(() => setIsArenaIdentified(true));
  }, [arenaId, identifyArena]);

  const {
    data: arenaData,
    loading: loadingArenaData,
    error: arenaDataError,
  } = useArenaPayeeSupportedPaymentMethodsQuery({
    variables: { arenaId },
    context: clientContext(),
  });

  // Fetch country data to resolve country names for address pre-population
  const { data: countryData, loading: loadingCountryData } = useGetCountryCodesQuery({
    context: clientContext(),
  });

  useReportError(arenaDataError ? formatError(arenaDataError) : null);

  // Bail on query errors before entering any loading state.
  if (arenaDataError) {
    const errorMessage = arenaDataError.message || "Unknown error occurred";
    toast.error(`Failed to load required data: ${errorMessage}`);
    return null;
  }

  // Wait for GraphQL data AND arena context establishment before evaluating
  // any flag-dependent gates. Without this guard, arena-scoped flags reflect
  // the unscoped Unleash default on the first render (before the useEffect fires).
  if (loadingArenaData || loadingCountryData || !isArenaIdentified) {
    return <LoadingIndicator />;
  }

  // Get the available payee payment methods for the related arena
  const arenaNode = arenaData?.arenas?.edges[0]?.node;
  if (!arenaNode) {
    toast.error("Arena payment methods not available");
    return null;
  }

  if (
    paymentPref &&
    paymentPref?.__typename !== "WirePaymentPrefObjectType" &&
    paymentPref?.__typename !== "AchPaymentPrefObjectType"
  ) {
    toast.error("Payment method not supported");
    return null;
  }

  const availablePaymentMethods = getAvailablePaymentMethods({
    availablePaymentMethods: arenaNode.payeeSupportedPaymentMethods,
  });

  const singleAvailablePaymentMethod = getSingleAvailablePaymentMethod(availablePaymentMethods);

  const arena: Arena = {
    id: arenaNode.id,
    features: {
      hasPositionPaymentPrefStaffApprovals:
        arenaNode.features?.hasPositionPaymentPrefStaffApprovals ?? false,
    },
  };

  const isEditMode = !!paymentPref;
  const initialPaymentMethod =
    isEditMode && paymentPref ? getPaymentMethod(paymentPref) : PaymentMethod.Wire;

  // Don't pre-populate initialPaymentPrefDetail - let the onBeforeContinue callback handle it
  const initialPaymentPrefDetail = isEditMode ? paymentPref : undefined;

  return (
    <ConsolidatedPaymentPrefProvider
      availablePaymentMethods={availablePaymentMethods}
      singleAvailablePaymentMethod={singleAvailablePaymentMethod}
      workflowMode={isEditMode ? WorkflowMode.Edit : WorkflowMode.Create}
      arena={arena}
      initialPaymentPrefDetail={initialPaymentPrefDetail}
      initialSelectedPaymentMethod={initialPaymentMethod}
    >
      <PayeeWorkflowWithContext
        paymentPref={paymentPref}
        payeeName={payeeName}
        payeeAddress={payeeAddress}
        countryData={countryData}
        isEditMode={isEditMode}
        onExit={onExit}
        onSuccess={onSuccess}
      />
    </ConsolidatedPaymentPrefProvider>
  );
};
